import { useState } from "react";

import { PH_CODES } from "../../lib/renterDrivers";

// Task 6: the one form for a renter driver's details (staff, customer, guest,
// invited driver). Only what eligibility needs; date of birth is optional and
// explained. Nothing typed here is ever "verified".
export function emptyDetails(name = "") {
  return {
    legal_name: name, date_of_birth: "", residency: "PH_RESIDENT", ph_entry_date: "",
    licence: { authority: "PH", classification: "NON_PROFESSIONAL", codes: ["B"], expiry_date: "", issue_date: "",
               first_licensed_date: "", number: "", country: "", english: true, foreign_categories: "" },
    idp: { use: false, categories: "B", expiry_date: "", issuing_country: "", convention: "VIENNA_1968" },
  };
}

export function toPayload(d) {
  const lic = d.licence;
  const foreign = lic.authority === "FOREIGN";
  const payload = {
    legal_name: d.legal_name.trim(),
    residency: foreign ? d.residency : "PH_RESIDENT",
    licence: {
      authority: lic.authority, expiry_date: lic.expiry_date || null, issue_date: lic.issue_date || null,
      first_licensed_date: lic.first_licensed_date || null,
      ...(lic.number ? { number: lic.number } : {}),
      ...(foreign
        ? { country: lic.country, english: !!lic.english,
            foreign_categories: lic.foreign_categories.split(",").map((c) => c.trim()).filter(Boolean) }
        : { classification: lic.classification, codes: lic.codes }),
    },
  };
  if (d.date_of_birth) payload.date_of_birth = d.date_of_birth;
  if (foreign && d.residency === "FOREIGN_VISITOR" && d.ph_entry_date) payload.ph_entry_date = d.ph_entry_date;
  if (foreign && d.idp.use) {
    payload.idp = { issuing_country: d.idp.issuing_country, convention: d.idp.convention,
                    categories: d.idp.categories.split(",").map((c) => c.trim()).filter(Boolean),
                    expiry_date: d.idp.expiry_date || null };
  }
  return payload;
}

export default function DriverDetailsForm({ value, onChange, idPrefix = "dd" }) {
  const [showOptional, setShowOptional] = useState(false);
  const d = value;
  const set = (patch) => onChange({ ...d, ...patch });
  const setLic = (patch) => onChange({ ...d, licence: { ...d.licence, ...patch } });
  const setIdp = (patch) => onChange({ ...d, idp: { ...d.idp, ...patch } });
  const foreign = d.licence.authority === "FOREIGN";
  const field = "mt-1 w-full min-w-0 px-3 py-2 rounded-lg border border-slate-300 text-sm";

  return (
    <div className="grid gap-3" data-testid={`${idPrefix}-form`}>
      <label className="text-sm">
        <span className="text-xs text-slate-600">Full name as on the licence</span>
        <input id={`${idPrefix}-name`} className={field} value={d.legal_name} onChange={(e) => set({ legal_name: e.target.value })} />
      </label>
      <fieldset className="text-sm">
        <legend className="text-xs text-slate-600">Licence issued in</legend>
        <div className="flex flex-wrap gap-3 mt-1">
          <label className="flex items-center gap-1"><input type="radio" checked={!foreign} onChange={() => setLic({ authority: "PH" })} /> Philippines</label>
          <label className="flex items-center gap-1"><input type="radio" checked={foreign} onChange={() => setLic({ authority: "FOREIGN" })} /> Another country</label>
        </div>
      </fieldset>
      {!foreign && (
        <>
          <label className="text-sm">
            <span className="text-xs text-slate-600">Licence type</span>
            <select className={field} value={d.licence.classification} onChange={(e) => setLic({ classification: e.target.value })}>
              <option value="NON_PROFESSIONAL">Non-professional</option>
              <option value="PROFESSIONAL">Professional</option>
              <option value="STUDENT_PERMIT">Student permit</option>
            </select>
          </label>
          <fieldset className="text-sm">
            <legend className="text-xs text-slate-600">DL codes on the card</legend>
            <div className="flex flex-wrap gap-2 mt-1">
              {PH_CODES.map((c) => (
                <label key={c} className="flex items-center gap-1 border rounded px-2 py-1 text-xs">
                  <input type="checkbox" checked={d.licence.codes.includes(c)}
                         onChange={() => setLic({ codes: d.licence.codes.includes(c) ? d.licence.codes.filter((x) => x !== c) : [...d.licence.codes, c] })} />
                  {c}
                </label>
              ))}
            </div>
          </fieldset>
        </>
      )}
      {foreign && (
        <div className="grid sm:grid-cols-2 gap-3">
          <label className="text-sm"><span className="text-xs text-slate-600">Country</span>
            <input className={field} value={d.licence.country} onChange={(e) => setLic({ country: e.target.value })} /></label>
          <label className="text-sm"><span className="text-xs text-slate-600">Categories on the licence (e.g. B)</span>
            <input className={field} value={d.licence.foreign_categories} onChange={(e) => setLic({ foreign_categories: e.target.value })} /></label>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={!!d.licence.english}
                 onChange={(e) => setLic({ english: e.target.checked })} /> The licence is in English</label>
          <label className="text-sm"><span className="text-xs text-slate-600">Status in the Philippines</span>
            <select className={field} value={d.residency} onChange={(e) => set({ residency: e.target.value })}>
              <option value="FOREIGN_VISITOR">Visitor</option>
              <option value="FOREIGN_RESIDENT">Resident</option>
              <option value="UNKNOWN">Not sure</option>
            </select></label>
          {d.residency === "FOREIGN_VISITOR" && (
            <label className="text-sm"><span className="text-xs text-slate-600">Date you arrived in the Philippines</span>
              <input type="date" className={field} value={d.ph_entry_date} onChange={(e) => set({ ph_entry_date: e.target.value })} /></label>
          )}
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={d.idp.use}
                 onChange={(e) => setIdp({ use: e.target.checked })} /> I have an International Driving Permit</label>
          {d.idp.use && (
            <>
              <label className="text-sm"><span className="text-xs text-slate-600">IDP categories (e.g. B)</span>
                <input className={field} value={d.idp.categories} onChange={(e) => setIdp({ categories: e.target.value })} /></label>
              <label className="text-sm"><span className="text-xs text-slate-600">IDP valid until</span>
                <input type="date" className={field} value={d.idp.expiry_date} onChange={(e) => setIdp({ expiry_date: e.target.value })} /></label>
            </>
          )}
        </div>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="text-sm"><span className="text-xs text-slate-600">Licence valid until</span>
          <input type="date" className={field} value={d.licence.expiry_date} onChange={(e) => setLic({ expiry_date: e.target.value })} /></label>
        <label className="text-sm"><span className="text-xs text-slate-600">Licence number</span>
          <input className={field} value={d.licence.number} autoComplete="off" onChange={(e) => setLic({ number: e.target.value })} /></label>
      </div>
      <button type="button" className="text-left text-xs underline text-slate-600" onClick={() => setShowOptional((s) => !s)}>
        {showOptional ? "Hide" : "Add"} date of birth and licence dates (only if this rental has an age or licence-history rule)
      </button>
      {showOptional && (
        <div className="grid sm:grid-cols-3 gap-3">
          <label className="text-sm"><span className="text-xs text-slate-600">Date of birth</span>
            <input type="date" className={field} value={d.date_of_birth} onChange={(e) => set({ date_of_birth: e.target.value })} /></label>
          <label className="text-sm"><span className="text-xs text-slate-600">Issued on</span>
            <input type="date" className={field} value={d.licence.issue_date} onChange={(e) => setLic({ issue_date: e.target.value })} /></label>
          <label className="text-sm"><span className="text-xs text-slate-600">First licensed</span>
            <input type="date" className={field} value={d.licence.first_licensed_date} onChange={(e) => setLic({ first_licensed_date: e.target.value })} /></label>
        </div>
      )}
    </div>
  );
}
